import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Steps, Typography, Space, Alert, Card, Divider } from 'antd';
import { WindowsOutlined, TeamOutlined, CheckCircleOutlined, RocketOutlined, SlackOutlined } from '@ant-design/icons';
import { create_workspace, getCookie } from '../backendApi/backend';
import MicrosoftAuth from './MicrosoftAuth';
import SlackAuth from './SlackAuth';

const { Title, Paragraph } = Typography;

interface FirstTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceCreated?: (workspaceData: any) => void;
}

const FirstTimeModal: React.FC<FirstTimeModalProps> = ({ 
  isOpen, 
  onClose, 
  onWorkspaceCreated 
}) => {
  const [currentStep, setCurrentStep] = useState<'welcome' | 'microsoft' | 'slack' | 'workspace' | 'complete'>('welcome');
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdWorkspace, setCreatedWorkspace] = useState<any>(null);
  const [isMicrosoftConnected, setIsMicrosoftConnected] = useState<boolean>(false);
  const [isSlackConnected, setIsSlackConnected] = useState<boolean>(false);

  useEffect(() => {
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isDev) {
      setIsMicrosoftConnected(true);
      setIsSlackConnected(true);
      if (currentStep === 'welcome') {
        setCurrentStep('workspace');
      }
      return;
    }

    // Check if user is already logged in with Microsoft and Slack
    const azureadToken = getCookie('azuread_token');
    const slackToken = getCookie('slack_access_token');
    
    if (azureadToken && azureadToken.trim() !== '') {
      setIsMicrosoftConnected(true);
    }
    
    if (slackToken && slackToken.trim() !== '') {
      setIsSlackConnected(true);
    }
    
    // If both are already connected, skip to workspace creation
    if (azureadToken && azureadToken.trim() !== '' && slackToken && slackToken.trim() !== '') {
      if (currentStep === 'welcome') {
        setCurrentStep('workspace');
      }
    }
  }, [isOpen, currentStep]);

  if (!isOpen) return null;

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      setError('Please enter a workspace name');
      return;
    }

    setIsCreatingWorkspace(true);
    setError(null);

    try {
      const workspaceData = await create_workspace(workspaceName.trim());
      setCreatedWorkspace(workspaceData);
      
      // Store the workspace ID in localStorage
      if (workspaceData.workspace_id) {
        localStorage.setItem('currentWorkspaceId', workspaceData.workspace_id);
      }

      onWorkspaceCreated?.(workspaceData);
      setCurrentStep('complete');
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      if (error.response?.status === 403 && error.response?.data?.error?.includes('SharePoint license is missing')) {
        setError('You do not have SPO license to create a workspace.');
      } else {
        setError(error.response?.data?.error || 'Failed to create workspace. Please try again.');
      }
    } finally {
      setIsCreatingWorkspace(false);
    }
  };

  const handleMicrosoftSuccess = () => {
    setIsMicrosoftConnected(true);
    if (isSlackConnected) {
      setCurrentStep('workspace');
    } else {
      setCurrentStep('slack');
    }
  };

  const handleSlackSuccess = () => {
    setIsSlackConnected(true);
    setCurrentStep('workspace');
  };

  const handleBackFromMicrosoft = () => {
    setCurrentStep('welcome');
  };

  const handleBackFromSlack = () => {
    setCurrentStep('microsoft');
  };

  const handleBackFromWorkspace = () => {
    setCurrentStep('slack');
  };

  const handleComplete = () => {
    onClose();
    // Refresh the page to ensure workspace is properly set
    window.location.reload();
  };

  const renderWelcomeStep = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '24px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <RocketOutlined style={{ 
          fontSize: '64px', 
          color: 'var(--primary-color)',
          filter: 'drop-shadow(0 4px 8px rgba(199, 255, 0, 0.3))'
        }} />
      </div>
      <Title level={2} style={{ 
        color: 'var(--text-dark)', 
        textAlign: 'center',
        marginBottom: '16px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, var(--text-dark), var(--text-medium))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        Welcome to Tommy!
      </Title>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        {isMicrosoftConnected && isSlackConnected
          ? "Great! You're already connected to Microsoft and Slack. Let's create your first workspace to start collaborating and organizing your work."
          : isMicrosoftConnected 
            ? "You're connected to Microsoft! Now let's set up Slack and create your workspace to enhance your collaboration experience."
            : isSlackConnected
              ? "You're connected to Slack! Now let's set up Microsoft integration and create your workspace for better file management."
              : "Let's get you set up with Microsoft and Slack authentication, then create your first workspace to start collaborating and organizing your work."
        }
      </Paragraph>
      <div style={{ marginTop: '32px' }}>
        <Button 
          type="primary"
          size="large"
          onClick={() => {
            if (isMicrosoftConnected && isSlackConnected) {
              setCurrentStep('workspace');
            } else if (isMicrosoftConnected) {
              setCurrentStep('slack');
            } else if (isSlackConnected) {
              setCurrentStep('microsoft');
            } else {
              setCurrentStep('microsoft');
            }
          }}
          style={{ 
            background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            padding: '0 32px',
            fontSize: '16px',
            boxShadow: '0 4px 15px rgba(199, 255, 0, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-light), var(--primary-lighter))';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(199, 255, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-color), var(--primary-light))';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(199, 255, 0, 0.3)';
          }}
        >
          {isMicrosoftConnected && isSlackConnected ? 'Create Workspace' : 'Get Started'}
        </Button>
      </div>
    </div>
  );

  const renderWorkspaceStep = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '24px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <TeamOutlined style={{ 
          fontSize: '64px', 
          color: 'var(--primary-color)',
          filter: 'drop-shadow(0 4px 8px rgba(199, 255, 0, 0.3))'
        }} />
      </div>
      <Title level={2} style={{ 
        color: 'var(--text-dark)', 
        textAlign: 'center',
        marginBottom: '16px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, var(--text-dark), var(--text-medium))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        Create Your Workspace
      </Title>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        A workspace helps you organize your projects and collaborate with your team.
        {(isMicrosoftConnected || isSlackConnected) && (
          <span style={{ 
            color: 'var(--success-color)', 
            fontWeight: 600,
            display: 'block',
            marginTop: '12px',
            background: 'linear-gradient(135deg, var(--success-color), #73d13d)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '15px'
          }}>
            ✨ Your {isMicrosoftConnected && isSlackConnected 
              ? 'Microsoft and Slack integrations will enable advanced collaboration features!' 
              : isMicrosoftConnected 
                ? 'Microsoft integration will enable advanced file management features!' 
                : 'Slack integration will enable enhanced team communication features!'}
          </span>
        )}
      </Paragraph>
      
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        textAlign: 'left',
        margin: '24px 0',
        maxWidth: '400px',
        width: '100%',
        alignSelf: 'center'
      }}>
        <label style={{ 
          color: 'var(--text-dark)', 
          fontWeight: 600,
          fontSize: '15px',
          fontFamily: 'inherit'
        }}>
          Workspace Name
        </label>
        <Input
          id="workspace-name"
          size="large"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          placeholder="e.g., My Company, Project Alpha"
          status={error ? 'error' : ''}
          disabled={isCreatingWorkspace}
          onPressEnter={handleCreateWorkspace}
          style={{ 
            borderRadius: '8px',
            fontSize: '16px',
            padding: '12px 16px',
            height: '48px',
            border: '1px solid #d9d9d9',
            transition: 'all 0.3s ease'
          }}
        />
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ 
              borderRadius: '8px',
              marginTop: '8px'
            }}
          />
        )}
      </div>

      <Space size="middle" style={{ marginTop: '32px' }}>
        <Button 
          type="default"
          size="large"
          onClick={handleBackFromWorkspace}
          disabled={isCreatingWorkspace}
          style={{
            height: '48px',
            borderRadius: '8px',
            padding: '0 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: '1px solid #d9d9d9',
            color: 'var(--text-medium)'
          }}
        >
          Back
        </Button>
        <Button 
          type="primary"
          size="large"
          onClick={handleCreateWorkspace}
          disabled={isCreatingWorkspace || !workspaceName.trim()}
          loading={isCreatingWorkspace}
          style={{ 
            background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            padding: '0 32px',
            fontSize: '16px',
            boxShadow: '0 4px 15px rgba(199, 255, 0, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            if (!isCreatingWorkspace && workspaceName.trim()) {
              e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-light), var(--primary-lighter))';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(199, 255, 0, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCreatingWorkspace && workspaceName.trim()) {
              e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-color), var(--primary-light))';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(199, 255, 0, 0.3)';
            }
          }}
        >
          {isCreatingWorkspace ? 'Creating...' : 'Create Workspace'}
        </Button>
      </Space>
    </div>
  );

  const renderMicrosoftStep = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '24px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <WindowsOutlined style={{ 
          fontSize: '64px', 
          color: '#0078d4',
          filter: 'drop-shadow(0 4px 8px rgba(0, 120, 212, 0.3))'
        }} />
      </div>
      <Title level={2} style={{ 
        color: 'var(--text-dark)', 
        textAlign: 'center',
        marginBottom: '16px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, var(--text-dark), var(--text-medium))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        Connect to Microsoft
      </Title>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        Connect your Microsoft account to access your OneDrive files, SharePoint documents, 
        and other Microsoft 365 services. This connection enhances file management in your workspace.
      </Paragraph>
      
      <Card style={{ 
        margin: '24px auto',
        maxWidth: '300px',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        boxShadow: 'var(--card-shadow)'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          padding: '8px'
        }}>
          <MicrosoftAuth disabled={false} />
        </div>
      </Card>

      <Space size="middle" style={{ marginTop: '32px' }}>
        <Button 
          type="default"
          size="large"
          onClick={handleBackFromMicrosoft}
          style={{
            height: '48px',
            borderRadius: '8px',
            padding: '0 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: '1px solid #d9d9d9',
            color: 'var(--text-medium)'
          }}
        >
          Back
        </Button>
        <Button 
          type="primary"
          size="large"
          onClick={handleMicrosoftSuccess}
          style={{ 
            background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            padding: '0 32px',
            fontSize: '16px',
            boxShadow: '0 4px 15px rgba(199, 255, 0, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-light), var(--primary-lighter))';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(199, 255, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-color), var(--primary-light))';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(199, 255, 0, 0.3)';
          }}
        >
          Continue
        </Button>
      </Space>
    </div>
  );

  const renderSlackStep = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '24px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <SlackOutlined style={{ 
          fontSize: '64px', 
          color: '#4A154B',
          filter: 'drop-shadow(0 4px 8px rgba(74, 21, 75, 0.3))'
        }} />
      </div>
      <Title level={2} style={{ 
        color: 'var(--text-dark)', 
        textAlign: 'center',
        marginBottom: '16px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, var(--text-dark), var(--text-medium))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        Connect to Slack
      </Title>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        Connect your Slack workspace to enable seamless team communication, 
        access your messages, and enhance collaboration within Tommy.
      </Paragraph>
      
      <Card style={{ 
        margin: '24px auto',
        maxWidth: '300px',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        boxShadow: 'var(--card-shadow)'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          padding: '8px'
        }}>
          <SlackAuth disabled={false} />
        </div>
      </Card>

      <Space size="middle" style={{ marginTop: '32px' }}>
        <Button 
          type="default"
          size="large"
          onClick={handleBackFromSlack}
          style={{
            height: '48px',
            borderRadius: '8px',
            padding: '0 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: '1px solid #d9d9d9',
            color: 'var(--text-medium)'
          }}
        >
          Back
        </Button>
        <Button 
          type="primary"
          size="large"
          onClick={handleSlackSuccess}
          style={{ 
            background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            padding: '0 32px',
            fontSize: '16px',
            boxShadow: '0 4px 15px rgba(199, 255, 0, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-light), var(--primary-lighter))';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(199, 255, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-color), var(--primary-light))';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(199, 255, 0, 0.3)';
          }}
        >
          Continue
        </Button>
      </Space>
    </div>
  );

  const renderCompleteStep = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      minHeight: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '24px'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <CheckCircleOutlined style={{ 
          fontSize: '64px', 
          color: 'var(--success-color)',
          filter: 'drop-shadow(0 4px 8px rgba(82, 196, 26, 0.3))'
        }} />
      </div>
      <Title level={2} style={{ 
        color: 'var(--text-dark)', 
        textAlign: 'center',
        marginBottom: '16px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, var(--text-dark), var(--text-medium))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        You're All Set!
      </Title>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        {createdWorkspace ? 
          `Your workspace "${createdWorkspace.name}" has been created successfully!` :
          'Welcome to Tommy!'
        }
      </Paragraph>
      <Paragraph style={{ 
        textAlign: 'center', 
        fontSize: '16px', 
        color: 'var(--text-medium)',
        lineHeight: '1.6',
        maxWidth: '400px',
        margin: '0 auto'
      }}>
        You can now start chatting and collaborating. Enjoy your experience!
      </Paragraph>
      
      <div style={{ marginTop: '32px' }}>
        <Button 
          type="primary"
          size="large"
          onClick={handleComplete}
          style={{ 
            background: 'linear-gradient(135deg, var(--primary-color), var(--primary-light))',
            borderColor: 'var(--primary-color)',
            color: 'var(--text-dark)',
            fontWeight: 600,
            height: '48px',
            borderRadius: '8px',
            padding: '0 32px',
            fontSize: '16px',
            boxShadow: '0 4px 15px rgba(199, 255, 0, 0.3)',
            border: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-light), var(--primary-lighter))';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(199, 255, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--primary-color), var(--primary-light))';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(199, 255, 0, 0.3)';
          }}
        >
          Start Using Tommy
        </Button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'welcome':
        return renderWelcomeStep();
      case 'microsoft':
        return renderMicrosoftStep();
      case 'slack':
        return renderSlackStep();
      case 'workspace':
        return renderWorkspaceStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return renderWelcomeStep();
    }
  };

  const getStepIndex = () => {
    if (isMicrosoftConnected && isSlackConnected) {
      // If both Microsoft and Slack are already connected, skip the auth steps
      switch (currentStep) {
        case 'welcome': return 0;
        case 'workspace': return 1;
        case 'complete': return 2;
        default: return 0;
      }
    } else if (isMicrosoftConnected || isSlackConnected) {
      // If one auth service is connected, show reduced steps
      switch (currentStep) {
        case 'welcome': return 0;
        case 'microsoft': return 1;
        case 'slack': return 1;
        case 'workspace': return 2;
        case 'complete': return 3;
        default: return 0;
      }
    } else {
      // If neither service is connected, show all steps
      switch (currentStep) {
        case 'welcome': return 0;
        case 'microsoft': return 1;
        case 'slack': return 2;
        case 'workspace': return 3;
        case 'complete': return 4;
        default: return 0;
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      closable={false}
      maskClosable={false}
      styles={{
        content: {
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '0',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <div style={{ 
        padding: '32px 32px 16px 32px',
        background: 'linear-gradient(135deg, rgba(199, 255, 0, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Steps
          current={getStepIndex()}
          size="small"
          items={[
            {
              title: 'Welcome',
              icon: <RocketOutlined />,
            },
            ...(isMicrosoftConnected && isSlackConnected ? [] : [
              ...(isMicrosoftConnected ? [] : [
                {
                  title: 'Microsoft',
                  icon: <WindowsOutlined />,
                }
              ]),
              ...(isSlackConnected ? [] : [
                {
                  title: 'Slack',
                  icon: <SlackOutlined />,
                }
              ]),
            ]),
            {
              title: 'Workspace',
              icon: <TeamOutlined />,
            },
            {
              title: 'Complete',
              icon: <CheckCircleOutlined />,
            },
          ]}
          style={{ 
            fontFamily: 'inherit'
          }}
        />
      </div>
      
      <div style={{ 
        backgroundColor: '#ffffff'
      }}>
        {renderCurrentStep()}
      </div>
    </Modal>
  );
};

export default FirstTimeModal;
